/**
 * List-oriented provisioning-setup access for home entry discovery.
 * Uses GET /provisioning-setup/access only — never the active /access endpoint.
 */

import { useCallback, useEffect, useState } from "react";
import {
  fetchProvisioningSetupAccess,
  type ProvisioningSetupAccessEntry,
} from "../api/cognitoryProvisioningSetupAccessClient";

export type ProvisioningSetupAccessListPhase =
  | "idle_signed_out"
  | "resolving"
  | "resolved"
  | "unauthenticated"
  | "temporary_failure"
  | "unable_to_verify";

export type ProvisioningSetupAccessListState = {
  phase: ProvisioningSetupAccessListPhase;
  cognitories: ProvisioningSetupAccessEntry[];
  requestId: string | null;
  retry: () => void;
};

export function hasProvisioningSetupDiscovery(
  state: ProvisioningSetupAccessListState,
): boolean {
  return state.phase === "resolved" && state.cognitories.length > 0;
}

export function isProvisioningSetupAccessUnverifiable(
  state: ProvisioningSetupAccessListState,
): boolean {
  return (
    state.phase === "unable_to_verify" ||
    state.phase === "temporary_failure" ||
    state.phase === "unauthenticated"
  );
}

export function useProvisioningSetupAccessList(input: {
  signedIn: boolean;
}): ProvisioningSetupAccessListState {
  const [phase, setPhase] = useState<ProvisioningSetupAccessListPhase>(
    input.signedIn ? "resolving" : "idle_signed_out",
  );
  const [cognitories, setCognitories] = useState<
    ProvisioningSetupAccessEntry[]
  >([]);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const retry = useCallback(() => {
    setReloadToken(value => value + 1);
  }, []);

  useEffect(() => {
    if (!input.signedIn) {
      setPhase("idle_signed_out");
      setCognitories([]);
      setRequestId(null);
      return;
    }

    let cancelled = false;
    setPhase("resolving");
    setCognitories([]);
    setRequestId(null);

    void (async () => {
      const result = await fetchProvisioningSetupAccess();
      if (cancelled) return;

      setRequestId(result.requestId);
      if (result.kind === "unauthenticated") {
        setPhase("unauthenticated");
        setCognitories([]);
        return;
      }
      if (result.kind === "temporary_failure") {
        setPhase("temporary_failure");
        setCognitories([]);
        return;
      }
      if (result.kind === "unable_to_verify") {
        setPhase("unable_to_verify");
        setCognitories([]);
        return;
      }
      if (result.kind === "resolved") {
        setCognitories(result.cognitories);
        setPhase("resolved");
        return;
      }

      setPhase("unable_to_verify");
      setCognitories([]);
    })();

    return () => {
      cancelled = true;
    };
  }, [input.signedIn, reloadToken]);

  return {
    phase,
    cognitories,
    requestId,
    retry,
  };
}
